"use client";

import { format, parseISO } from "date-fns";
import { enUS, zhCN } from "date-fns/locale";
import { Activity, Zap, Clock } from "lucide-react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

import { MetricCard } from "@/components/dashboard/metric-card";
import { getFeatureDemoCopy } from "@/components/features/feature-demo-copy";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/lib/components/card";
import { generateMockActivityData, mockMetrics } from "@/lib/mock-feature-data";

import { useI18n } from "@llmgateway/i18n/react";

function ChartTooltipContent({
	active,
	payload,
	requestsLabel,
}: {
	active?: boolean;
	payload?: any;
	requestsLabel: string;
}) {
	if (active && payload && payload.length) {
		return (
			<div className="rounded-lg border bg-popover text-popover-foreground p-2 shadow-sm">
				<p className="font-medium">{payload[0].payload.formattedDate}</p>
				<p className="text-sm">
					<span className="font-medium">{payload[0].value}</span>{" "}
					{requestsLabel}
				</p>
			</div>
		);
	}
	return null;
}

export function PerformanceMonitoringDemo() {
	const { locale } = useI18n();
	const copy = getFeatureDemoCopy(locale).performanceMonitoring;
	const dateFnsLocale = locale === "zh-CN" ? zhCN : enUS;
	const data = generateMockActivityData();

	const chartData = data.activity.map((day) => ({
		...day,
		formattedDate: format(parseISO(day.date), copy.dateFormat, {
			locale: dateFnsLocale,
		}),
	}));

	return (
		<div className="space-y-6">
			<div className="grid gap-4 md:grid-cols-3">
				<MetricCard
					label={copy.totalRequests}
					value={mockMetrics.totalRequests}
					subtitle={copy.last7Days}
					icon={<Activity className="h-4 w-4" />}
					accent="blue"
				/>
				<MetricCard
					label={copy.avgLatency}
					value={mockMetrics.avgLatency}
					subtitle={copy.meanResponseTime}
					icon={<Clock className="h-4 w-4" />}
					accent="purple"
				/>
				<MetricCard
					label={copy.cacheHitRate}
					value={mockMetrics.cacheHitRate}
					subtitle={copy.cachedResponses}
					icon={<Zap className="h-4 w-4" />}
					accent="green"
				/>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>{copy.requestActivity}</CardTitle>
					<CardDescription>{copy.requestActivityDescription}</CardDescription>
				</CardHeader>
				<CardContent>
					<ResponsiveContainer width="100%" height={300}>
						<BarChart data={chartData}>
							<CartesianGrid strokeDasharray="3 3" vertical={false} />
							<XAxis
								dataKey="formattedDate"
								stroke="#888888"
								fontSize={12}
								tickLine={false}
								axisLine={false}
							/>
							<YAxis
								stroke="#888888"
								fontSize={12}
								tickLine={false}
								axisLine={false}
							/>
							<Tooltip
								content={<ChartTooltipContent requestsLabel={copy.requests} />}
								cursor={{
									fill: "color-mix(in srgb, currentColor 15%, transparent)",
								}}
							/>
							<Bar
								dataKey="requestCount"
								fill="currentColor"
								radius={[4, 4, 0, 0]}
								className="fill-primary"
							/>
						</BarChart>
					</ResponsiveContainer>
				</CardContent>
			</Card>
		</div>
	);
}
